import PropTypes from "prop-types";
import { useEffect, useState } from "react";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import SimpleDoubleChecking from "@/Layouts/components/Buttons/SimpleDoubleChecking";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import TierAddHeader from "@Layouts/components/Tiers/TierAddHeader";

// MUI
import { Stack, SvgIcon, Typography } from "@mui/material";

// ICONS
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { ReactComponent as TrashIcon } from "@/assets/icons/actions/trash.svg";

// Constants
const DELETE_COLOR = "var(--gray-blue-color)";
const DEFAULT_PAYMENT_OPTIONS = [
	{
		type: "Customer",
		label: "Customer Pay",
	},
	{
		type: "Internal",
		label: "Internal Pay",
	},
	{
		type: "Warranty",
		label: "Warranty Pay",
	},
];
const HEADER_ATTRIBUTES = {
	component: <HeaderComponent />,
	dividerProperties: {
		show: true,
		sx: {
			bottom: "-5px",
			marginLeft: "-15px",
		},
	},
};
const SX_DIVIDER = {
	bottom: "-5px",
	marginLeft: "-15px",
	height: "122px !important",
};

const TitleHeader = (props) => {
	const { title = "", classes = "!font-montserrat !font-[500] !text-[16px] text-default text-center" } = props;
	return (
		<Typography variant="h5" component="div" className={classes}>
			{title}
		</Typography>
	);
};
TitleHeader.propTypes = {
	title: PropTypes.string,
	classes: PropTypes.string,
};

const SelectorHeader = (props) => {
	const { index = 0, options = [], readOnly = false, onDeleteAction = () => {}, onSelectAction = () => {} } = props;

	return (
		<Stack sx={{ minWidth: "115px" }}>
			<Stack className="h-[48px]"></Stack>
			<Stack width={1}>
				<SmartSelector
					showBy={"label"}
					selectBy={"type"}
					options={options}
					selected={null}
					disabled={readOnly}
					error={true}
					inputProps={{
						placeholder: "Select Type",
					}}
					listBoxProps={{
						sx: { maxHeight: "185px" },
					}}
					popperProps={{
						sx: { minWidth: "115px", maxWidth: "115px" },
					}}
					onChangeAction={onSelectAction}
				/>
			</Stack>
			<Stack display={"row"} justifyContent={"center"} alignItems={"center"}>
				<SimpleDoubleChecking
					className={"btn-delete"}
					color={DELETE_COLOR}
					confirmationButtonColor={DELETE_COLOR}
					icon={<CloseRoundedIcon fontSize="small" />}
					iconHover={<CloseRoundedIcon fontSize="small" />}
					confirmationIcon={
						<SvgIcon
							className="[&>path]:fill-gray-blue !text-gray-blue !text-[18px] !ml-[1px]"
							component={TrashIcon}
							viewBox={"0 0 14 14"}
							color={"primary"}
						/>
					}
					disabled={readOnly}
					onDeleteAction={() => onDeleteAction(index)}
				/>
			</Stack>
		</Stack>
	);
};
SelectorHeader.propTypes = {
	index: PropTypes.number,
	options: PropTypes.array,
	readOnly: PropTypes.bool,
	onDeleteAction: PropTypes.func,
	onSelectAction: PropTypes.func,
};

const PayHeader = (props) => {
	const {
		index = 0,
		title = "",
		classes = "",
		checked = false,
		disabled = false,
		readOnly = false,
		switchDisabled = false,
		onCheckAction = () => {},
		onDeleteAction = () => {},
	} = props;

	return (
		<Stack sx={{ minWidth: "115px" }}>
			<Stack>
				<CustomizedSwitches
					key={"newAndPreOwnedRequired"}
					labelPlacement={"top"}
					textChecked={"Global"}
					textUnChecked={"Global"}
					checked={checked}
					disabled={readOnly || switchDisabled}
					onChange={onCheckAction}
					tooltip={{
						title: "Make global pay.",
						placement: "bottom",
						key: "default-commissions-new-and-preOwned-tooltip",
					}}
				/>
			</Stack>
			<Stack justifyContent={"center"} alignItems={"center"}>
				<TitleHeader title={title} classes={classes} />
			</Stack>
			<Stack display={"row"} justifyContent={"center"} alignItems={"center"}>
				<SimpleDoubleChecking
					className={"btn-delete"}
					color={DELETE_COLOR}
					confirmationButtonColor={DELETE_COLOR}
					icon={<CloseRoundedIcon fontSize="small" />}
					iconHover={<CloseRoundedIcon fontSize="small" />}
					confirmationIcon={
						<SvgIcon
							className="[&>path]:fill-gray-blue !text-gray-blue !text-[18px] !ml-[1px]"
							component={TrashIcon}
							viewBox={"0 0 14 14"}
							color={"primary"}
						/>
					}
					disabled={disabled || readOnly}
					onDeleteAction={() => onDeleteAction(index)}
				/>
			</Stack>
		</Stack>
	);
};
PayHeader.propTypes = {
	index: PropTypes.number,
	title: PropTypes.string,
	classes: PropTypes.string,
	checked: PropTypes.bool,
	disabled: PropTypes.bool,
	readOnly: PropTypes.bool,
	switchDisabled: PropTypes.bool,
	onCheckAction: PropTypes.func,
	onDeleteAction: PropTypes.func,
};

function HeaderComponent(props) {
	const {
		index = 0,
		title = "Comm.",
		payType = "",
		payLabel = "",
		classes = "!font-montserrat !font-[500] !text-[16px] text-default",
		actions = undefined,
		dependencies = undefined,
		global = false,
		readOnly = false,
		switchDisabled = false,
	} = props;

	const { remove = undefined, update = undefined } = actions || {};
	const { disabled = false, onClickAction: onDeleteAction = () => {} } = remove || {};
	const { onClickAction: onUpdateAction = () => {} } = update || {};
	const { options = [] } = dependencies || {};

	const [payment, setPayment] = useState(null);

	const handleChangeLabel = (selected) => {
		const { label, type } = selected || {};
		setPayment({ ...payment, label, type });
		onUpdateAction(index, { global: Boolean(payment?.global), payLabel: label, payType: type });
	};

	const handleCheck = (value) => {
		const { label, type } = payment || {};
		setPayment({ ...payment, global: value });
		onUpdateAction(index, { global: value, payLabel: label, payType: type });
	};

	useEffect(() => {
		setPayment({
			label: payLabel,
			type: payType,
			global,
		});

		return () => {
			setPayment(null);
		};
	}, [payLabel, payType, global]);

	return (
		<Stack justifyContent={"center"} alignItems={"center"}>
			{!payment?.label ? (
				<SelectorHeader
					index={index}
					options={options}
					readOnly={readOnly}
					onSelectAction={handleChangeLabel}
					onDeleteAction={onDeleteAction}
				/>
			) : (
				<PayHeader
					index={index}
					title={payment?.label}
					label={payment?.label}
					classes={classes}
					checked={Boolean(payment?.global)}
					disabled={disabled}
					readOnly={readOnly}
					switchDisabled={switchDisabled}
					onCheckAction={handleCheck}
					onDeleteAction={onDeleteAction}
				/>
			)}

			{payment?.label && (
				<Stack>
					<TitleHeader title={title} classes={classes} />
				</Stack>
			)}
		</Stack>
	);
}
HeaderComponent.propTypes = {
	index: PropTypes.number,
	title: PropTypes.string,
	payLabel: PropTypes.string,
	payType: PropTypes.string,
	classes: PropTypes.string,
	actions: PropTypes.object,
	dependencies: PropTypes.object,
	global: PropTypes.bool,
	readOnly: PropTypes.bool,
	switchDisabled: PropTypes.bool,
};

const newHeader = () => ({
	title: "Comm.",
	payType: "",
	payLabel: "",
	global: false,
	...HEADER_ATTRIBUTES,
});

const isSomePayDefined = (headers = []) => headers.some(({ payType }) => !!payType);
const isSomePayUndefined = (headers = []) => headers.some(({ payType }) => !payType);

const mappedHeaders = (headers = []) => {
	if (!headers.length) {
		return [];
	}

	const hasSomePayDefined = isSomePayDefined(headers);
	return headers.map((header, index) => {
		const mappedHeader = { ...header, ...HEADER_ATTRIBUTES };

		if (!index) {
			return mappedHeader;
		}

		const { payType = "", dividerProperties } = mappedHeader || {};

		if (!payType && !hasSomePayDefined) {
			return mappedHeader;
		}

		return { ...mappedHeader, dividerProperties: { ...dividerProperties, sx: SX_DIVIDER } };
	});
};

const getUniqueOptions = (headers = []) => {
	const selected = headers.map(({ payType }) => payType);
	return DEFAULT_PAYMENT_OPTIONS.filter(({ type }) => !selected.includes(type));
};

const getMappedHeaders = (headers = [], keys = ["title"]) => {
	return headers.map((header) => {
		return keys.reduce((acc, key) => {
			acc[key] = header?.[key];
			return acc;
		}, {});
	});
};

export default function TierGrossHeader(props) {
	const {
		title = "Gross Header",
		headers = [],
		mapKeys = [],
		maxHeaders = 3,
		readOnly = false,
		onActionAdd = () => {},
		onActionDelete = () => {},
		onActionUpdate = () => {},
	} = props;

	const [innerHeaders, setInnerHeaders] = useState([]);
	const isAddDisabled = innerHeaders.length >= maxHeaders;

	const handleDeleteHeader = (index) => {
		const updatedHeaders = [...innerHeaders];
		const headerToDelete = innerHeaders[index - 1];

		updatedHeaders.splice(index - 1, 1);
		setInnerHeaders(updatedHeaders);

		if (headerToDelete?.payType) {
			onActionDelete?.(getMappedHeaders(updatedHeaders, mapKeys));
		}
	};

	const handleUpdateHeader = (index, updated) => {
		const updatedHeaders = [...innerHeaders];
		const prevHeader = updatedHeaders[index - 1];
		updatedHeaders[index - 1] = { ...prevHeader, ...updated };
		setInnerHeaders(updatedHeaders);
		onActionUpdate?.(getMappedHeaders(updatedHeaders, mapKeys));
	};

	const handleAddHeader = () => {
		setInnerHeaders([...innerHeaders, newHeader()]);
		onActionAdd?.();
	};

	useEffect(() => {
		setInnerHeaders(headers);
		return () => {
			setInnerHeaders([]);
		};
	}, [headers]);

	const addedHeaders = mappedHeaders([...innerHeaders]);
	const hasSomeUndefined = isSomePayUndefined(innerHeaders);
	const options = getUniqueOptions(innerHeaders);

	return (
		<TierAddHeader
			headers={[{ title, properties: { sx: { marginTop: "46px" } } }, ...addedHeaders]}
			actions={{
				remove: {
					disabled: innerHeaders.length === 1 || hasSomeUndefined || readOnly,
					onClickAction: handleDeleteHeader,
				},
				update: {
					onClickAction: handleUpdateHeader,
				},
			}}
			dependencies={{ options }}
			disabledAddButton={isAddDisabled || hasSomeUndefined || readOnly}
			readOnly={readOnly}
			onActionAdd={handleAddHeader}
		/>
	);
}
TierGrossHeader.propTypes = {
	title: PropTypes.string,
	headers: PropTypes.array,
	mapKeys: PropTypes.array,
	maxHeaders: PropTypes.number,
	readOnly: PropTypes.bool,
	onActionAdd: PropTypes.func,
	onActionDelete: PropTypes.func,
	onActionUpdate: PropTypes.func,
};
